import { useCallback, useEffect, useState } from "react";
import { QuizAnswer } from "@verity/api-client/types";
import apiClient from "../../../../helpers/api-client";
import { TakeQuizQuestion } from "../components/TakeQuizPage";

export type QuizResponse = {
	/** DB row id — required for updateQuizResponses */
	id: number;
	answer: QuizAnswer | null;
	isAnswered: boolean;
};

// ── LocalStorage ──────────────────────────────────────────────────────────────

type PersistedQuizSession = {
	sessionId: string;
	questions: TakeQuizQuestion[];
	responses: QuizResponse[];
	currentIndex: number;
};

type PersistedScore = { earned: number; total: number };

function scoreStorageKey(sessionId: string) {
	return `quiz-score-${sessionId}`;
}

function saveScoreToStorage(sessionId: string, earned: number, total: number) {
	try {
		localStorage.setItem(scoreStorageKey(sessionId), JSON.stringify({ earned, total } satisfies PersistedScore));
	} catch {
		// ignore
	}
}

export function readScoreFromStorage(sessionId: string): PersistedScore | null {
	try {
		const raw = localStorage.getItem(scoreStorageKey(sessionId));
		return raw ? (JSON.parse(raw) as PersistedScore) : null;
	} catch {
		return null;
	}
}

function storageKey(sessionId: string) {
	return `quiz-session-${sessionId}`;
}

function loadFromStorage(sessionId: string): PersistedQuizSession | null {
	try {
		const raw = localStorage.getItem(storageKey(sessionId));
		if (!raw) return null;
		return JSON.parse(raw) as PersistedQuizSession;
	} catch {
		return null;
	}
}

function saveToStorage(data: PersistedQuizSession) {
	try {
		localStorage.setItem(storageKey(data.sessionId), JSON.stringify(data));
	} catch {
		// Storage quota exceeded — fail silently
	}
}

function removeFromStorage(sessionId: string) {
	try {
		localStorage.removeItem(storageKey(sessionId));
	} catch {
		// ignore
	}
}

type UseQuizInfoProps = {
	proctoringSessionId: string;
};

export function useQuizInfo({ proctoringSessionId }: UseQuizInfoProps) {
	const [questions, setQuestions] = useState<TakeQuizQuestion[]>([]);
	const [responses, setResponses] = useState<QuizResponse[]>([]);
	const [currentIndex, setCurrentIndex] = useState(0);
	const [isLoadingResponses, setIsLoadingResponses] = useState(false);

	// Load state: localStorage first, then API
	useEffect(() => {
		let cancelled = false;
		void (async () => {
			setIsLoadingResponses(true);
			try {
				// 1. Check localStorage cache first
				const cached = loadFromStorage(proctoringSessionId);
				if (cached) {
					if (!cancelled) {
						setQuestions(cached.questions);
						setResponses(cached.responses);
						setCurrentIndex(cached.currentIndex);
					}
					return;
				}

				// 2. Fall back to API
				const data = await apiClient.proctoringSession.getStudentQuizSessionData(proctoringSessionId);
				if (cancelled) return;

				const filteredQuestions = data.questions.filter((q) => q.questionType !== "FileUpload");
				const sortedQuestions = [...filteredQuestions].sort(
					(a, b) => a.questionOrder - b.questionOrder,
				) as TakeQuizQuestion[];

				const mappedResponses: QuizResponse[] = data.responses
					.slice()
					.sort((a, b) => a.questionNo - b.questionNo)
					.map((r) => ({
						id: r.id,
						answer: (r.answer as QuizAnswer | null) ?? null,
						isAnswered: r.isAnswered,
					}));

				setQuestions(sortedQuestions);
				setResponses(mappedResponses);
				setCurrentIndex(0);
			} catch (err) {
				console.error("Failed to load quiz session data:", err);
			} finally {
				if (!cancelled) setIsLoadingResponses(false);
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [proctoringSessionId]);

	// Persist to localStorage on every meaningful state change
	useEffect(() => {
		if (questions.length === 0) return;
		saveToStorage({ sessionId: proctoringSessionId, questions, responses, currentIndex });
	}, [proctoringSessionId, questions, responses, currentIndex]);

	const updateResponse = useCallback((index: number, answer: QuizAnswer) => {
		setResponses((prev) =>
			prev.map((r, i) => {
				if (i !== index) return r;
				return { ...r, answer, isAnswered: true };
			}),
		);
	}, []);

	/** Flushes all responses to the backend, saves the final score, and clears the session cache. */
	const submitResponses = useCallback(async () => {
		const res = await apiClient.proctoringSession.updateQuizResponses(proctoringSessionId, {
			responses: responses.map((r) => ({
				id: r.id,
				answer: r.answer,
				isAnswered: r.isAnswered,
			})),
		});

		// Persist the final score before wiping the session so the complete screen can read it
		const earned = Math.round(res.responses.reduce((sum, r) => sum + (r.grading?.score ?? 0), 0) * 100) / 100;
		const total = res.questions.reduce((sum, q) => sum + q.correctionPoints, 0);
		saveScoreToStorage(proctoringSessionId, earned, total);
		removeFromStorage(proctoringSessionId);

		return { earnedPoints: earned, totalPoints: total };
	}, [proctoringSessionId, responses]);

	const totalPoints = questions.reduce((sum, q) => sum + q.correctionPoints, 0);

	return {
		questions,
		responses,
		isLoadingResponses,
		updateResponse,
		currentIndex,
		setCurrentIndex,
		submitResponses,
		totalPoints,
	};
}
